import React, { CSSProperties, ReactElement, useEffect, useState } from 'react';
import { get } from '../../clients/AuthClient';
import { ModalAlert } from '../../types/modal-alert';
import { FlexBox, Table, Spinner, colors } from '@cimpress/react-components';
import { HistoryRecord } from '../../types/history-record';

export interface HistoryColumnData {
  key: string,
  displayName: string,
}

interface GenericHistoryViewProps {
  displayName: string,
  entityId: string,
  entityType: string,
  tenant: string,
  recordsStartDate?: Date,
  columns: HistoryColumnData[],
  showAlert: (alert: ModalAlert) => void,
}
export default function GenericHistoryView({ displayName, entityId, entityType, tenant, recordsStartDate, columns, showAlert }: GenericHistoryViewProps ): ReactElement {
  const [historyRows, setHistoryRows] = useState([] as HistoryRecord[]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    console.log(showAlert);
    setIsLoading(true);
    get<HistoryRecord[]>(`${process.env.REACT_APP_PRESSINTEGRATION_SERVICE_URL}/v1/Tenant/${tenant}/History?entityId=${entityId}&entityType=${entityType}`)
      .then(historyResponse => {
        setHistoryRows(historyResponse);
      }).catch(e => {
        console.error(e);
        showAlert({
          message: e.message,
          title: 'Received an Error from the Press Intgeratin Service',
          type: 'danger'
        });
      }).finally(() => {
        setIsLoading(false);
      });
  }, []);

  const getCellDisplay = (rawData: any, previousRawData: any | undefined, columnData: HistoryColumnData) => {
    const data = rawData[columnData.key] ?? '';
    const dataStringified = data ? JSON.stringify(data, null, 2) : '';
    const previousData = previousRawData?.[columnData.key] ?? '';
    const previousDataStringified = previousData ? JSON.stringify(previousData, null, 2) : '';
    const isChanged = dataStringified !== previousDataStringified;
    const changedStyle: CSSProperties = isChanged ? {
      color: 'green',
      fontWeight: 'bolder',
    } : {};
    if (typeof data === 'object') {
      return <div style={{maxHeight: 200, overflowY: 'auto'}}>
        <code style={{color: 'unset', ...changedStyle}}>
          <pre style={{border: 'none'}}>
            {dataStringified}
          </pre>
        </code>
      </div>;
    }
    return <div style={{textWrap: 'nowrap', ...changedStyle}}>
      {data}
    </div>;
  };

  const historyColumns = [
    { Header: 'Date', accessor: 'date' },
    { Header: 'Changed By', accessor: 'changedBy' },
    ...columns.map(c => ({ Header: c.displayName, accessor: c.key, Cell: (row: any) => getCellDisplay(row.original.rawData, row.original.previousRawData, c) })),
    { 
      Header: 'Raw Data',
      accessor: 'rawData',
      Cell: (row: any) => 
        <div style={{maxHeight: 200, overflowY: 'auto'}}>
          <code>
            <pre style={{border: 'none'}}>
              {JSON.stringify(row.original.rawData, null, 2)}
            </pre>
          </code>
        </div> 
    },
  ];

  return (
    <>
      {isLoading &&
        <FlexBox justifyContent="center">
          <Spinner size='medium' />
        </FlexBox>
      }
      <h3>
        {displayName} <small style={{color: colors.cobalt.base, fontSize: '0.6em'}}>({entityId})</small>
      </h3>
      <div className="row">
        <div className="col-12">
          <Table
            columns={historyColumns}
            data={historyRows.map((h, index) => ({
              rawData: JSON.parse(h.jsonData),
              previousRawData: historyRows[index + 1] ? JSON.parse(historyRows[index + 1].jsonData) : undefined,
              date: new Date(Date.parse(h.timestamp)).toLocaleString(),
              changedBy: h.user,
            }))}
            noDataText={'No history data found'}
          />
          {recordsStartDate && <em>Note: History before {recordsStartDate.toLocaleDateString()} was not recorded.</em>}
        </div>
      </div>
    </>
  );
}
